// Paysol offline store — thin IndexedDB wrapper.
// Two object stores:
//   products_cache  keyPath=variant_id — cached variant + stock rows for offline POS
//   pending_sales   keyPath=id (UUID)  — sales queued while offline, drained by sync.js

const DB_NAME = 'paysol';
const DB_VERSION = 1;

let _dbPromise = null;

function openDb() {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB not supported'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains('products_cache')) {
        const s = db.createObjectStore('products_cache', { keyPath: 'variant_id' });
        s.createIndex('by_shop', 'shop_id', { unique: false });
      }
      if (!db.objectStoreNames.contains('pending_sales')) {
        const s = db.createObjectStore('pending_sales', { keyPath: 'id' });
        s.createIndex('by_state', 'sync_state', { unique: false });
        s.createIndex('by_created', 'created_at', { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror   = () => reject(req.error);
  });
  return _dbPromise;
}

function tx(storeName, mode = 'readonly') {
  return openDb().then((db) => db.transaction(storeName, mode).objectStore(storeName));
}

// ---- products_cache ----
export async function cacheProducts(rows) {
  const store = await tx('products_cache', 'readwrite');
  await new Promise((resolve, reject) => {
    const clearReq = store.clear();
    clearReq.onsuccess = () => {
      rows.forEach((r) => store.put(r));
      resolve();
    };
    clearReq.onerror = () => reject(clearReq.error);
  });
}

export async function loadCachedProducts(shopId = null) {
  const store = await tx('products_cache');
  return new Promise((resolve, reject) => {
    const results = [];
    const req = store.openCursor();
    req.onsuccess = (e) => {
      const cur = e.target.result;
      if (cur) {
        if (shopId == null || cur.value.shop_id === shopId) results.push(cur.value);
        cur.continue();
      } else {
        resolve(results);
      }
    };
    req.onerror = () => reject(req.error);
  });
}

// ---- pending_sales ----
function uuid() {
  return 'p_' + Date.now() + '_' + Math.random().toString(16).slice(2, 10);
}

export async function queueSale(salePayload) {
  const row = {
    id: uuid(),
    payload: salePayload,
    created_at: new Date().toISOString(),
    sync_state: 'pending',
    error: null,
    attempts: 0,
    // Local receipt no format so cashier sees something
    local_receipt_no: 'OFF-' + new Date().toISOString().slice(0, 10).replace(/-/g, '') + '-' + Math.random().toString(16).slice(2, 6).toUpperCase(),
  };
  const store = await tx('pending_sales', 'readwrite');
  await new Promise((resolve, reject) => {
    const req = store.add(row);
    req.onsuccess = () => resolve();
    req.onerror   = () => reject(req.error);
  });
  return row;
}

export async function listPending() {
  const store = await tx('pending_sales');
  return new Promise((resolve, reject) => {
    const req = store.getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror   = () => reject(req.error);
  });
}

export async function countPending() {
  const store = await tx('pending_sales');
  return new Promise((resolve, reject) => {
    const req = store.index('by_state').count(IDBKeyRange.only('pending'));
    req.onsuccess = () => resolve(req.result || 0);
    req.onerror   = () => reject(req.error);
  });
}

export async function markSyncing(id) {
  return _update(id, (row) => { row.sync_state = 'syncing'; row.attempts = (row.attempts || 0) + 1; });
}
export async function markFailed(id, error) {
  return _update(id, (row) => { row.sync_state = 'failed'; row.error = String(error || '?').slice(0, 400); });
}
export async function removePending(id) {
  const store = await tx('pending_sales', 'readwrite');
  return new Promise((resolve, reject) => {
    const req = store.delete(id);
    req.onsuccess = () => resolve();
    req.onerror   = () => reject(req.error);
  });
}
export async function retryFailed() {
  const store = await tx('pending_sales', 'readwrite');
  return new Promise((resolve, reject) => {
    const req = store.getAll();
    req.onsuccess = () => {
      (req.result || []).forEach((r) => {
        if (r.sync_state === 'failed') { r.sync_state = 'pending'; r.error = null; store.put(r); }
      });
      resolve();
    };
    req.onerror = () => reject(req.error);
  });
}
async function _update(id, mutator) {
  const store = await tx('pending_sales', 'readwrite');
  return new Promise((resolve, reject) => {
    const get = store.get(id);
    get.onsuccess = () => {
      const row = get.result;
      if (!row) return resolve();
      mutator(row);
      const put = store.put(row);
      put.onsuccess = () => resolve();
      put.onerror   = () => reject(put.error);
    };
    get.onerror = () => reject(get.error);
  });
}
